const VIEWPORT_HEIGHT_VAR = '--beacon-viewport-height';
const VIEWPORT_OFFSET_TOP_VAR = '--beacon-viewport-offset-top';
const VIEWPORT_OFFSET_LEFT_VAR = '--beacon-viewport-offset-left';

const noop = () => {};

const canUseWindow = () => typeof window !== 'undefined';

const canUseDocument = () => typeof document !== 'undefined';

const canUseWindowEvents = () => (
  canUseWindow() &&
  typeof window.addEventListener === 'function' &&
  typeof window.removeEventListener === 'function'
);

const getViewport = () => {
  if (!canUseWindow()) {
    return undefined;
  }

  return window.visualViewport;
}

const forceAppShellRepaint = () => {
  if (!canUseDocument() || !document.body) {
    return;
  }

  const previousTransform = document.body.style.transform;
  document.body.style.transform = 'translateZ(0)';
  window.requestAnimationFrame?.(() => {
    document.body.style.transform = previousTransform;
  });
}

export const syncIOSViewportState = () => {
  if (!canUseWindow() || !canUseDocument()) {
    return;
  }

  const viewport = getViewport();
  const height = viewport?.height || window.innerHeight;
  const offsetTop = viewport?.offsetTop || 0;
  const offsetLeft = viewport?.offsetLeft || 0;

  document.documentElement.style.setProperty(VIEWPORT_HEIGHT_VAR, `${height}px`);
  document.documentElement.style.setProperty(VIEWPORT_OFFSET_TOP_VAR, `${offsetTop}px`);
  document.documentElement.style.setProperty(VIEWPORT_OFFSET_LEFT_VAR, `${offsetLeft}px`);

  window.scrollTo(0, 0);
  forceAppShellRepaint();
}

export const installIOSViewportRestoreHandler = () => {
  if (!canUseWindowEvents()) {
    return noop;
  }

  let frame: number | undefined;
  let timeout: number | undefined;

  const scheduleSync = () => {
    if (typeof frame === 'number') {
      window.cancelAnimationFrame?.(frame);
    }
    if (typeof timeout === 'number') {
      window.clearTimeout(timeout);
    }

    frame = window.requestAnimationFrame?.(() => {
      frame = undefined;
      syncIOSViewportState();
      timeout = window.setTimeout(() => {
        timeout = undefined;
        syncIOSViewportState();
      }, 120);
    });

    if (typeof frame !== 'number') {
      syncIOSViewportState();
    }
  }

  const syncWhenVisible = () => {
    if (!canUseDocument() || !document.hidden) {
      scheduleSync();
    }
  }

  syncIOSViewportState();

  window.addEventListener('pageshow', scheduleSync);
  window.addEventListener('focus', scheduleSync);
  window.addEventListener('resize', scheduleSync);
  window.addEventListener('orientationchange', scheduleSync);
  if (canUseDocument()) {
    document.addEventListener('visibilitychange', syncWhenVisible);
  }
  window.visualViewport?.addEventListener('resize', scheduleSync);
  window.visualViewport?.addEventListener('scroll', scheduleSync);

  return () => {
    if (typeof frame === 'number') {
      window.cancelAnimationFrame?.(frame);
    }
    if (typeof timeout === 'number') {
      window.clearTimeout(timeout);
    }

    window.removeEventListener('pageshow', scheduleSync);
    window.removeEventListener('focus', scheduleSync);
    window.removeEventListener('resize', scheduleSync);
    window.removeEventListener('orientationchange', scheduleSync);
    if (canUseDocument()) {
      document.removeEventListener('visibilitychange', syncWhenVisible);
    }
    window.visualViewport?.removeEventListener('resize', scheduleSync);
    window.visualViewport?.removeEventListener('scroll', scheduleSync);
  };
}
